// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { DSNSnapshot, Dish } from './types';

export interface DishTracking {
  dish: Dish;
  complexCode: string;
  complexName: string;
  spacecraftCodes: string[];
}

export interface TrackingPair {
  dish: string;
  complex: string;
  complexName: string;
  spacecraft: string;
  bands: string[];
  dataRates: number[];
  activeDown: boolean;
  activeUp: boolean;
}

export interface HistoryEvent {
  ts: number;
  dish: string;
  complex: string;
  spacecraft: string;
}

export function extractPairs(snapshot: DSNSnapshot): TrackingPair[] {
  const pairs: TrackingPair[] = [];
  for (const complex of snapshot.complexes) {
    for (const dish of complex.dishes) {
      const bySpacecraft = new Map<string, TrackingPair>();
      for (const signal of dish.signals) {
        if (!signal.spacecraft) continue;
        let pair = bySpacecraft.get(signal.spacecraft);
        if (!pair) {
          pair = {
            dish: dish.name,
            complex: complex.code,
            complexName: complex.friendlyName,
            spacecraft: signal.spacecraft,
            bands: [],
            dataRates: [],
            activeDown: false,
            activeUp: false,
          };
          bySpacecraft.set(signal.spacecraft, pair);
        }
        if (signal.band && !pair.bands.includes(signal.band)) pair.bands.push(signal.band);
        if (signal.dataRate > 0) pair.dataRates.push(signal.dataRate);
        if (signal.active && signal.direction === 'down') pair.activeDown = true;
        if (signal.active && signal.direction === 'up') pair.activeUp = true;
      }
      for (const target of dish.targets) {
        if (!target.name || target.name === '') continue;
        if (!bySpacecraft.has(target.name)) {
          bySpacecraft.set(target.name, {
            dish: dish.name,
            complex: complex.code,
            complexName: complex.friendlyName,
            spacecraft: target.name,
            bands: [],
            dataRates: [],
            activeDown: false,
            activeUp: false,
          });
        }
      }
      pairs.push(...bySpacecraft.values());
    }
  }
  return pairs;
}

export function uniqueSpacecraft(pairs: TrackingPair[]): string[] {
  const set = new Set<string>();
  for (const pair of pairs) {
    if (pair.spacecraft && pair.spacecraft !== 'DSN' && pair.spacecraft !== 'DSS') {
      set.add(pair.spacecraft);
    }
  }
  return [...set].sort();
}

export function activeDishCount(snapshot: DSNSnapshot): number {
  let count = 0;
  for (const complex of snapshot.complexes) {
    for (const dish of complex.dishes) {
      if (dish.activity.toLowerCase().includes('spacecraft')) count++;
    }
  }
  return count;
}

export function totalDownlinkRate(snapshot: DSNSnapshot): number {
  let total = 0;
  for (const complex of snapshot.complexes) {
    for (const dish of complex.dishes) {
      for (const signal of dish.signals) {
        if (signal.direction === 'down' && signal.active && signal.dataRate > 0) {
          total += signal.dataRate;
        }
      }
    }
  }
  return total;
}

export function highestRTLT(snapshot: DSNSnapshot): { name: string; rtlt: number } | null {
  let best: { name: string; rtlt: number } | null = null;
  for (const complex of snapshot.complexes) {
    for (const dish of complex.dishes) {
      for (const target of dish.targets) {
        if (target.rtlt > 0 && (!best || target.rtlt > best.rtlt)) {
          best = { name: target.name, rtlt: target.rtlt };
        }
      }
    }
  }
  return best;
}

export interface SessionState {
  history: HistoryEvent[];
  spacecraftSeen: Set<string>;
  firstSeen: number;
}

export function createSession(): SessionState {
  return { history: [], spacecraftSeen: new Set(), firstSeen: Date.now() };
}

export function updateSession(session: SessionState, snapshot: DSNSnapshot): void {
  const pairs = extractPairs(snapshot);
  for (const pair of pairs) {
    if (!pair.spacecraft || pair.spacecraft === 'DSN' || pair.spacecraft === 'DSS') continue;
    session.spacecraftSeen.add(pair.spacecraft);
    const lastMatching = [...session.history].reverse().find(
      (e) => e.dish === pair.dish && e.spacecraft === pair.spacecraft,
    );
    if (!lastMatching || lastMatching.ts !== snapshot.timestampUTC) {
      session.history.push({
        ts: snapshot.timestampUTC,
        dish: pair.dish,
        complex: pair.complex,
        spacecraft: pair.spacecraft,
      });
      if (session.history.length > 500) {
        session.history.splice(0, session.history.length - 500);
      }
    }
  }
}
